import { omit, sortBy } from "lodash";
import { NextRouter } from "next/router";

import { BadgeType } from "~/endpoints/trustFramework/types";
import { Country } from "~/pages/network/search";
import { JurisdictionStatusReadable, VaspDueDiligenceReviewStatus } from "~/templates/companies/types";
import { JurisdictionStatus, VASPRegulatoryStatus } from "~/types/vaspid";

export type FiltersConfig = {
  sort: "registration_desc" | "registration_asc" | "alphabetical" | "recommended";
  activity: "all_data_transfers" | "receiving_data_transfers" | "sending_data_transfers";
  regulatory_status: "all_status" | VASPRegulatoryStatus;
  badges: "all_badges" | BadgeType;
  jurisdictions: string;
  review_status: "all_review_status" | VaspDueDiligenceReviewStatus | "NULL";
  jurisdiction_status: "all_jurisdiction_status" | JurisdictionStatus;
};

export const SORT = [
  {
    label: "A-Z",
    value: "alphabetical",
  },
  {
    label: "Newest to oldest",
    value: "registration_desc",
  },
  {
    label: "Oldest to newest",
    value: "registration_asc",
  },
];

export const ACTIVITY = [
  {
    label: "Activity: All",
    value: "all_data_transfers",
  },
  {
    label: "Receiving data transfers",
    value: "receiving_data_transfers",
  },
  {
    label: "Sending data transfers",
    value: "sending_data_transfers",
  },
];

export const REGULATORY_STATUS = [
  {
    label: "Regulatory status: All",
    value: "all_status",
  },
  {
    label: "Exempt",
    value: "Exempt",
  },
  {
    label: "No longer exempt",
    value: "No longer exempt",
  },
  {
    label: "Not Regulated",
    value: "Not Regulated",
  },
  {
    label: "No longer regulated",
    value: "No longer regulated",
  },
  {
    label: "Pending",
    value: "Pending",
  },
  {
    label: "Regulated",
    value: "Regulated",
  },
];

export const BADGES = [
  {
    label: "Badges: All",
    value: "all_badges",
  },
  {
    label: "Super VASP",
    value: "super_vasp",
  },
  {
    label: "Verified",
    value: "verified",
  },
  {
    label: "In-Network",
    value: "in_network",
  },
  {
    label: "Claimed",
    value: "claimed",
  },
];

export const DUE_DILIGENCE_REVIEW_STATUS = [
  {
    label: "Review status: All",
    value: "all_review_status",
  },
  {
    label: "Trusted List",
    value: VaspDueDiligenceReviewStatus.TRUSTED,
  },
  {
    label: "Blocked List",
    value: VaspDueDiligenceReviewStatus.BLOCKED,
  },
  {
    label: "Manually Review Transactions",
    value: VaspDueDiligenceReviewStatus.MANUAL,
  },
  {
    label: "Not Reviewed",
    value: "NULL",
  },
];

export const JURISDICTION_STATUS: {
  label: string;
  value: JurisdictionStatus | "all_jurisdiction_status";
}[] = [
  {
    label: "Jurisdiction status: All",
    value: "all_jurisdiction_status",
  },
  {
    label: JurisdictionStatusReadable.inForce,
    value: "inForce",
  },
  {
    label: JurisdictionStatusReadable.limitedScope,
    value: "limitedScope",
  },
  {
    label: JurisdictionStatusReadable.gracePeriodOngoing,
    value: "gracePeriodOngoing",
  },
  {
    label: JurisdictionStatusReadable.plansAnnounced,
    value: "plansAnnounced",
  },
  {
    label: JurisdictionStatusReadable.notAvailable,
    value: "notAvailable",
  },
];

export const getAllJurisdictionsOptions = (countries: Country[]) =>
  [
    {
      label: "Jurisdictions: All",
      value: "all_jurisdictions",
    },
  ].concat(
    sortBy(countries, (c) => c.name).map((country) => ({
      label: country.name,
      value: country.id,
    })),
  );

export const DEFAULT_FILTERS: FiltersConfig = {
  sort: "alphabetical",
  activity: "all_data_transfers",
  regulatory_status: "all_status",
  badges: "all_badges",
  jurisdictions: "all_jurisdictions",
  review_status: "all_review_status",
  jurisdiction_status: "all_jurisdiction_status",
};

export function replaceQueryFilters({
  router,
  filtersConfig,
  clearResults,
}: {
  router: NextRouter;
  filtersConfig: FiltersConfig;
  clearResults: boolean;
}) {
  const removableKeysFromUrl = clearResults
    ? Object.keys(filtersConfig).concat(["subsidiariesOf", "currentPage"])
    : Object.keys(filtersConfig);
  const nextQuery = Object.keys(filtersConfig).reduce((prev, k) => {
    return filtersConfig[k as keyof FiltersConfig] === DEFAULT_FILTERS[k as keyof FiltersConfig]
      ? prev
      : { ...prev, [k]: filtersConfig[k as keyof FiltersConfig] };
  }, omit(router.query, removableKeysFromUrl));

  router.replace({ pathname: router.pathname, query: nextQuery }, undefined, {
    scroll: false,
  });
}

export const INCLUDE_IN_WIDGET_OPTIONS = [
  {
    label: "YES",
    value: "YES",
  },
  {
    label: "NO",
    value: "NO",
  },
];
